import { CURRENCY_OPTIONS } from "../constants/CURRENCY_OPTIONS";

// ============================================================================
// TIME CONVERSION UTILITIES
// ============================================================================

// Convert 12-hour time to 24-hour format
export const convertTime12To24 = (time12) => {
  if (!time12) return "";

  const [time, period] = time12.split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

// Convert 24-hour time to 12-hour format
export const convertTime24To12 = (time24) => {
  if (!time24) return "";

  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;

  return `${hours12.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
};

// Convert 12-hour time to minutes since midnight
export const convertTime12ToMinutes = (time12) => {
  if (!time12) return 0;

  const time24 = convertTime12To24(time12);
  const [hours, minutes] = time24.split(":").map(Number);
  return hours * 60 + minutes;
};

// Convert minutes since midnight to 12-hour format
export const convertMinutesToTime12 = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const time24 = `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
  return convertTime24To12(time24);
};

// ============================================================================
// TIME OPTIONS FOR DROPDOWNS
// ============================================================================

export const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const minutes = i * 15;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const time24 = `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
  return {
    value24: time24,
    display: convertTime24To12(time24),
    minutes: minutes,
  };
});

// ============================================================================
// TIME SLOT VALIDATION
// ============================================================================

export const validateTimeSlots = (slots) => {
  if (!slots || slots.length === 0) {
    return { isValid: true, conflicts: [] };
  }

  const conflicts = [];
  const sortedSlots = [...slots].sort(
    (a, b) => convertTime12ToMinutes(a.start) - convertTime12ToMinutes(b.start),
  );

  for (let i = 0; i < sortedSlots.length; i++) {
    const currentSlot = sortedSlots[i];
    const startMinutes = convertTime12ToMinutes(currentSlot.start);
    const endMinutes = convertTime12ToMinutes(currentSlot.end);

    // Check if start time is before end time
    if (startMinutes >= endMinutes) {
      conflicts.push({
        slotId: currentSlot.id,
        message: "Start time must be before end time",
        type: "invalid_range",
      });
    }

    // Check for overlaps with next slot
    if (i < sortedSlots.length - 1) {
      const nextSlot = sortedSlots[i + 1];
      const nextStartMinutes = convertTime12ToMinutes(nextSlot.start);

      if (endMinutes > nextStartMinutes) {
        conflicts.push({
          slotId: currentSlot.id,
          otherSlotId: nextSlot.id,
          message: "Time slots overlap",
          type: "overlap",
        });
      }
    }
  }

  return {
    isValid: conflicts.length === 0,
    conflicts,
  };
};

// ============================================================================
// AVAILABLE TIME OPTIONS (filtered based on existing slots)
// ============================================================================

export const getAvailableTimeOptions = (
  slots,
  currentSlotId,
  field,
  currentValue,
) => {
  if (!slots || slots.length === 0) return TIME_OPTIONS;

  const currentSlot = slots.find((slot) => slot.id === currentSlotId);
  if (!currentSlot) return TIME_OPTIONS;

  const otherSlots = slots.filter((slot) => slot.id !== currentSlotId);

  if (field === "start") {
    // For start time, we need to ensure it doesn't conflict with other slots
    return TIME_OPTIONS.filter((option) => {
      const optionMinutes = option.minutes;
      const currentEndMinutes = convertTime12ToMinutes(currentSlot.end);

      // Must be before current end time
      if (optionMinutes >= currentEndMinutes) return false;

      // Check against other slots
      for (const otherSlot of otherSlots) {
        const otherStartMinutes = convertTime12ToMinutes(otherSlot.start);
        const otherEndMinutes = convertTime12ToMinutes(otherSlot.end);

        // Can't start during another slot
        if (
          optionMinutes >= otherStartMinutes &&
          optionMinutes < otherEndMinutes
        ) {
          return false;
        }
      }

      return true;
    });
  } else {
    // For end time
    const currentStartMinutes = convertTime12ToMinutes(currentSlot.start);

    return TIME_OPTIONS.filter((option) => {
      const optionMinutes = option.minutes;

      // Must be after current start time
      if (optionMinutes <= currentStartMinutes) return false;

      // Check against other slots
      for (const otherSlot of otherSlots) {
        const otherStartMinutes = convertTime12ToMinutes(otherSlot.start);

        // Can't end after the start of the next slot
        if (
          optionMinutes > otherStartMinutes &&
          currentStartMinutes < otherStartMinutes
        ) {
          return false;
        }
      }

      return true;
    });
  }
};

// ============================================================================
// AVAILABILITY CONVERSION
// ============================================================================

// Day mapping
const DAY_MAPPING = {
  Mon: "monday",
  Tue: "tuesday",
  Wed: "wednesday",
  Thu: "thursday",
  Fri: "friday",
  Sat: "saturday",
  Sun: "sunday",
};

const REVERSE_DAY_MAPPING = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

// Convert frontend availability format to backend working_hours format
export const convertAvailabilityToWorkingHours = (availability) => {
  const workingHours = {};

  Object.entries(availability).forEach(([shortDay, dayData]) => {
    const fullDay = DAY_MAPPING[shortDay];
    workingHours[fullDay] = {
      enabled: dayData.enabled,
      slots: dayData.slots.map((slot) => ({
        start: convertTime12To24(slot.start),
        end: convertTime12To24(slot.end),
      })),
    };
  });

  return workingHours;
};

// Convert backend working_hours format to frontend availability format
export const convertWorkingHoursToAvailability = (workingHours) => {
  const availability = {
    Mon: { enabled: false, slots: [] },
    Tue: { enabled: false, slots: [] },
    Wed: { enabled: false, slots: [] },
    Thu: { enabled: false, slots: [] },
    Fri: { enabled: false, slots: [] },
    Sat: { enabled: false, slots: [] },
    Sun: { enabled: false, slots: [] },
  };

  if (!workingHours) return availability;

  Object.entries(workingHours).forEach(([fullDay, dayData]) => {
    const shortDay = REVERSE_DAY_MAPPING[fullDay];
    if (shortDay) {
      availability[shortDay] = {
        enabled: dayData.enabled || false,
        slots: (dayData.slots || []).map((slot, index) => ({
          id: `${shortDay}-${index}-${Date.now()}`,
          start: convertTime24To12(slot.start),
          end: convertTime24To12(slot.end),
        })),
      };
    }
  });

  return availability;
};

// ============================================================================
// TIMESLOT DURATION PARSING
// ============================================================================

export const parseSlotDuration = (timeslotDuration) => {
  const durationMatch = timeslotDuration.match(/\d+/);
  return durationMatch ? parseInt(durationMatch[0]) : 30;
};

export const formatSlotDuration = (minutes) => {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours * 60} minutes`;
    }
  }
  return `${minutes} minutes`;
};

// ============================================================================
// LOAD SETTINGS DATA
// ============================================================================

// LOAD SETTINGS DATA
export const loadSettingsData = async () => {
  try {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));

    if (!token || !user || !user.veterinary) {
      throw new Error("User not authenticated or veterinary data not found");
    }

    const veterinary = user.veterinary;

    // Convert working hours to availability format
    const availability = convertWorkingHoursToAvailability(
      veterinary.working_hours,
    );

    // Format timeslot duration
    const timeslotDuration = formatSlotDuration(veterinary.slot_duration || 30);

    const formData = {
      fullName: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
      email: user.email || "",
      contact: user.phone_number || "",
      officeAddress: user.address || "",
      bio: veterinary.bio || "",
      currency: veterinary.currency?.toUpperCase() || "USD",
      timeslotDuration: timeslotDuration,
      availability: availability,
      photoUrl: user.photo_url || null, // Add this line
    };

    return {
      formData,
      currencyOptions: CURRENCY_OPTIONS,
    };
  } catch (error) {
    console.error("Error loading settings data:", error);
    throw error;
  }
};

// ============================================================================
// UPDATE VETERINARY SETTINGS
// ============================================================================

// UPDATE VETERINARY SETTINGS
export const updateVeterinarySettings = async (
  formData,
  photoFile = null,
  deletePhoto = false,
) => {
  try {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token || token === "null" || token === "undefined") {
      throw new Error("Authentication required. Please login first.");
    }

    // Validate time slots
    const validationErrors = [];
    Object.entries(formData.availability).forEach(([day, dayData]) => {
      if (dayData.enabled) {
        const { isValid, conflicts } = validateTimeSlots(dayData.slots);
        if (!isValid) {
          validationErrors.push({
            day,
            conflicts,
          });
        }
      }
    });

    if (validationErrors.length > 0) {
      const errorMessages = validationErrors
        .map(
          (error) =>
            `• ${error.day}: ${error.conflicts.map((c) => c.message).join(", ")}`,
        )
        .join("\n");
      throw new Error(`Invalid time slots found:\n${errorMessages}`);
    }

    // Convert availability to working hours
    const workingHours = convertAvailabilityToWorkingHours(
      formData.availability,
    );

    // Parse slot duration
    const slotDuration = parseSlotDuration(formData.timeslotDuration);

    // Prepare payload
    const payload = {
      veterinary: {
        currency: formData.currency.toLowerCase(),
        bio: formData.bio,
        slot_duration: slotDuration,
        working_hours: workingHours,
      },
      user: {
        first_name: formData.fullName.split(" ")[0] || "",
        last_name: formData.fullName.split(" ").slice(1).join(" ") || "",
        phone_number: formData.contact || "",
        address: formData.officeAddress || "",
      },
    };

    // Handle photo deletion
    if (deletePhoto) {
      payload.user.delete_photo = "true";
    }

    // Create FormData for multipart/form-data if we have a photo
    let requestBody;
    let headers = {
      Authorization: `Bearer ${token}`,
    };

    if (photoFile) {
      const formDataObj = new FormData();

      // Add veterinary data
      formDataObj.append("veterinary[currency]", payload.veterinary.currency);
      formDataObj.append("veterinary[bio]", payload.veterinary.bio);
      formDataObj.append(
        "veterinary[slot_duration]",
        payload.veterinary.slot_duration,
      );

      // Add working hours
      Object.entries(payload.veterinary.working_hours).forEach(
        ([day, data]) => {
          formDataObj.append(
            `veterinary[working_hours][${day}][enabled]`,
            data.enabled,
          );
          data.slots.forEach((slot, index) => {
            formDataObj.append(
              `veterinary[working_hours][${day}][slots][${index}][start]`,
              slot.start,
            );
            formDataObj.append(
              `veterinary[working_hours][${day}][slots][${index}][end]`,
              slot.end,
            );
          });
        },
      );

      // Add user data
      formDataObj.append("user[first_name]", payload.user.first_name);
      formDataObj.append("user[last_name]", payload.user.last_name);
      formDataObj.append("user[phone_number]", payload.user.phone_number);
      formDataObj.append("user[address]", payload.user.address);

      // Add photo
      formDataObj.append("user[photo]", photoFile);

      if (deletePhoto) {
        formDataObj.append("user[delete_photo]", "true");
      }

      requestBody = formDataObj;
      // Don't set Content-Type header - browser will set it with boundary
    } else {
      requestBody = JSON.stringify(payload);
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/veterinaries/update_settings`,
      {
        method: "PATCH",
        headers: headers,
        body: requestBody,
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors || "Failed to update settings");
    }

    const data = await response.json();

    // UPDATE LOCALSTORAGE WITH NEW DATA
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);

        // Update the user object with new veterinary data
        const updatedUser = {
          ...user,
          first_name: payload.user.first_name,
          last_name: payload.user.last_name,
          phone_number: payload.user.phone_number,
          address: payload.user.address,
          photo_url: data.photo_url,
          veterinary: {
            ...user.veterinary,
            currency: formData.currency.toLowerCase(),
            bio: formData.bio,
            slot_duration: slotDuration,
            working_hours: workingHours,
            updated_at: new Date().toISOString(),
          },
        };

        // Save updated user back to localStorage
        localStorage.setItem("user", JSON.stringify(updatedUser));

        console.log("Updated user in localStorage:", updatedUser);
      } catch (parseError) {
        console.error("Failed to update localStorage:", parseError);
      }
    }

    return {
      success: true,
      message: "Settings updated successfully!",
      photo_url: data.photo_url,
      data,
    };
  } catch (error) {
    console.error("Error in updateVeterinarySettings:", error);
    throw error;
  }
};
